<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import { eqsApi } from '@/api/eqs';
import { notify } from '@/services/notice';
import { errorMessage } from '@/utils/format';

/**
 * 审批流转路径图。
 *
 * 「提交审批」以前是个黑盒：点下去成没成、下一个是谁、卡在哪儿全靠报错猜。
 * 这里把整条路画出来——**画的是实际会走的那条**：
 * 挂了审批流走审批流，没挂但有评审角色按角色在项目团队里找人（一级），都没有就不用在系统里审（0924）。
 *
 * 三种用法：
 * - 带 projectId + deliverableId：**这一条交付物**的真实路径，含谁批过、现在等谁
 * - 带 projectId + nodeId：这个计划节点的路径（节点下可能挂多条交付物，进度是估的）
 * - 带 steps：纯模板路径（系统设置里看审批流定义用），不查人不查状态
 */
interface RouteAssignee {
  userId: number;
  name: string;
  signed: boolean;
  /** 按现在的团队，这一级不该有他了——待办是提交那一刻定格的 */
  stale: boolean;
}
interface RouteStep {
  order: number;
  roleLabel: string;
  mode: 'any' | 'all';
  people: string[];
  resolvedFrom: 'team' | 'company' | 'named' | 'none';
  /** skipped = 这一级只会派到提交人本人，提交 / 推进时整级跳过（1006「审批链跳过提交人本人」） */
  state: 'done' | 'current' | 'pending' | 'skipped';
  skippedReason?: string;
  outcome: string | null;
  /**
   * 这一级每个人写的审批意见（0914 Tony 第 10 条:「评审意见写完,通过或驳回后不可见」）。
   * 意见一直存在库里,只是从来没被读回来过——签完就找不到了,
   * 而「附加条件通过」这种结论,意见才是全部内容。
   */
  decisions?: Array<{
    who: string | null;
    decision: 'approved' | 'rejected';
    at: string;
    comment: string | null;
  }>;
  /** 实际派出去的待办；有它就以它为准，`people` 只是「按现在的团队算应该是谁」 */
  assignees?: RouteAssignee[];
  signedCount?: number;
  /** 这一级点名了、但本项目团队里没人担的角色——派人时被静默丢掉的那些 */
  unfilledRoles?: string[];
}
interface Route {
  mechanism: 'workflow' | 'reviewers' | 'manual';
  mechanismLabel: string;
  workflowName: string | null;
  steps: RouteStep[];
  blockers: string[];
  /** 当前级里有人的待办和现在的团队对不上 */
  needsReassign?: boolean;
}

const props = defineProps<{
  projectId?: string | number;
  nodeId?: string | number;
  /** 给了它就按这条交付物取——比按节点准：一个节点下可能有好几条交付物 */
  deliverableId?: string | number;
  /** 模板模式：直接给步骤，不请求接口 */
  steps?: RouteStep[];
  compact?: boolean;
  /** 有写权限才给「重新派发」——这是改别人待办的动作 */
  writable?: boolean;
}>();

const emit = defineEmits<{ reassigned: [] }>();

const route = ref<Route | null>(null);
const loading = ref(false);
const error = ref('');

async function load(): Promise<void> {
  if (props.steps) {
    route.value = {
      mechanism: 'workflow', mechanismLabel: '', workflowName: null,
      steps: props.steps, blockers: [],
    };
    return;
  }
  if (props.projectId === undefined) return;
  if (props.deliverableId === undefined && props.nodeId === undefined) return;
  loading.value = true;
  error.value = '';
  try {
    const payload = props.deliverableId !== undefined
      ? await eqsApi.deliverableApprovalRoute(props.projectId, props.deliverableId)
      : await eqsApi.approvalRoute(props.projectId, props.nodeId!);
    route.value = payload as unknown as Route;
  } catch (caught) {
    route.value = null;
    error.value = errorMessage(caught);
  } finally {
    loading.value = false;
  }
}

watch(
  () => [props.projectId, props.nodeId, props.deliverableId, props.steps],
  load,
  { immediate: true, deep: true },
);

const MODE_LABEL: Readonly<Record<string, string>> = { any: '任一人通过', all: '需全部通过' };
const FROM_LABEL: Readonly<Record<string, string>> = {
  team: '', company: '跨项目找到', named: '', none: '派不到人',
};

const hasSteps = computed(() => Boolean(route.value?.steps.length));

/**
 * 当前级里「按现在的团队已经不该是他」的人。
 *
 * 0905 Tony 报的秦岭OP3：肖娟 8-28 提交时是这个项目的项目经理，会签第 1 级点名
 * 「项目经理」把待办派给了她；后来她不在这个团队了，待办还挂着。
 * 会签要全员签完才推进——她不签，这条就永远卡在第 1 级，后面几级的人也等不到。
 */
const staleNames = computed(() => {
  const current = route.value?.steps.find((step) => step.state === 'current');
  return (current?.assignees ?? []).filter((person) => person.stale && !person.signed)
    .map((person) => person.name);
});

/**
 * 有角色空着的那几级。
 *
 * 0908 Tony 看 DH26003 的「团队会签」发现只派到 1 个人：这一级点名 9 个角色，
 * 项目团队只配了 2 个，其余 7 个在派人时被**静默丢掉**——单子照走、少签 7 个，
 * 界面上一个字都没有。会签是「全员签完才通过」，少的这几个签名不会有人补上，
 * 所以要把缺谁写出来，并指到能修的地方（团队页补人 → 重新派发）。
 */
const gapSteps = computed(() =>
  (route.value?.steps ?? []).filter((step) => step.unfilledRoles?.length));

const reassigning = ref(false);

async function reassign(): Promise<void> {
  if (props.projectId === undefined || props.deliverableId === undefined) return;
  reassigning.value = true;
  try {
    const result = await eqsApi.reassignDeliverableApproval(props.projectId, props.deliverableId);
    const parts: string[] = [];
    if (result.removed.length) parts.push(`撤掉 ${result.removed.join('、')}`);
    if (result.added.length) parts.push(`补上 ${result.added.join('、')}`);
    notify(
      'success',
      parts.length ? `第 ${result.step} 级已重新派发` : `第 ${result.step} 级本来就是对的`,
      parts.length ? parts.join('，') : '按现在的团队算，这一级的人没有变化。',
    );
    await load();
    emit('reassigned');
  } catch (caught) {
    notify('error', '重新派发失败', errorMessage(caught));
  } finally {
    reassigning.value = false;
  }
}
</script>

<template>
  <section class="route-diagram">
    <header v-if="!compact && route?.mechanismLabel">
      <b>{{ route.mechanismLabel }}</b>
      <small v-if="route.workflowName">{{ route.workflowName }}</small>
    </header>

    <p v-if="loading" class="route-hint">正在算流转路径…</p>
    <p v-else-if="error" class="route-hint route-error">读取失败：{{ error }}</p>

    <template v-else-if="hasSteps">
      <ol class="route-track">
        <li
          v-for="(step, index) in route!.steps"
          :key="step.order"
          class="route-step"
          :class="[`is-${step.state}`, { 'no-people': !step.people.length }]"
        >
          <i v-if="index > 0" class="route-arrow" aria-hidden="true">→</i>
          <div class="route-card">
            <span class="route-order">第 {{ step.order }} 级</span>
            <b>{{ step.roleLabel }}</b>
<!--
              派过待办的级按**实际派给谁**画：待办才是决定谁能点「通过」的东西，
              重算只是「按现在的团队应该是谁」。两边不同源，就会出现
              「图上这一级没有你、你却收到待审批」（0905 秦岭OP3）。
            -->
            <span v-if="step.assignees?.length" class="route-people">
              <span
                v-for="person in step.assignees"
                :key="person.userId"
                class="route-person"
                :class="{ signed: person.signed, stale: person.stale && !person.signed }"
                :title="person.signed ? '已出结论'
                  : person.stale ? '按现在的项目团队，这一级不该有他——他是提交那一刻定格进来的' : '还没出结论'"
              >{{ person.signed ? '✓ ' : '' }}{{ person.name }}</span>
            </span>
            <span v-else class="route-people">
              <template v-if="step.state === 'skipped'">
                <s>{{ step.people.join('、') }}</s>
                <b class="route-skip-flag" :title="step.skippedReason">提交人本人 · 跳过</b>
              </template>
              <template v-else-if="step.people.length">{{ step.people.join('、') }}</template>
              <em v-else>派不到人</em>
            </span>
            <small>
              {{ MODE_LABEL[step.mode] }}
              <template v-if="step.mode === 'all' && step.assignees?.length">
                （已签 {{ step.signedCount ?? 0 }}/{{ step.assignees.length }}）
              </template>
              <template v-if="FROM_LABEL[step.resolvedFrom]"> · {{ FROM_LABEL[step.resolvedFrom] }}</template>
              <template v-if="step.unfilledRoles?.length">
                · <b class="route-gap-flag">{{ step.unfilledRoles.length }} 个角色空岗</b>
              </template>
              <template v-if="step.outcome"> · {{ step.outcome }}</template>
            </small>
            <!--
              审批意见（0914 第 10 条）。摆在这一级底下,而不是折起来:
              「通过,但供应商要在 9/30 前补检测报告」这类结论,意见就是全部内容,
              收起来等于没有。没写意见的不占位置。
            -->
            <ul v-if="step.decisions?.some((item) => item.comment)" class="route-comments">
              <li v-for="(item, index) in step.decisions!.filter((entry) => entry.comment)" :key="index">
                <b :class="item.decision">{{ item.who ?? '有人' }}{{ item.decision === 'approved' ? '通过' : '退回' }}</b>
                <span>{{ item.comment }}</span>
                <em>{{ item.at }}</em>
              </li>
            </ul>
            <span v-if="step.state === 'current'" class="route-now">现在这里</span>
          </div>
        </li>
      </ol>

      <ul v-if="route!.blockers.length" class="route-blockers">
        <li v-for="blocker in route!.blockers" :key="blocker">{{ blocker }}</li>
      </ul>

      <!-- 点名了却没人担的角色：不写出来就是静默少签 -->
      <div v-if="gapSteps.length" class="route-gaps">
        <b>有 {{ gapSteps.length }} 级点名的角色，本项目团队里没人担</b>
        <ul>
          <li v-for="step in gapSteps" :key="step.order">
            第 {{ step.order }} 级空岗 {{ step.unfilledRoles!.length }} 个：{{ step.unfilledRoles!.join('、') }}
          </li>
        </ul>
        <span>
          这些角色不会有人收到审批，也就不会有人签。
          <template v-if="gapSteps.some((step) => step.mode === 'all')">
            会签算「全员签完才通过」，缺的这几个签名不会自己补上——
          </template>
          去「团队」页把这些角色配上人<template v-if="deliverableId !== undefined">，
          再点下面的「按现在的团队重新派发」</template>。
        </span>
      </div>

      <!-- 待办和现在的团队对不上：说清楚卡在哪，并给一个能修的动作 -->
      <div v-if="route!.needsReassign && staleNames.length" class="route-stale">
        <b>{{ staleNames.join('、') }} 已经不在这一级的角色名单里</b>
        <span>
          待办是提交那一刻派的，之后项目团队变过。他手上还挂着这条的审批，
          <template v-if="route!.steps.find((step) => step.state === 'current')?.mode === 'all'">
            而这一级要全员签完才往下走——不处理就会一直卡在这里。
          </template>
          <template v-else>按现在的团队重新派一次即可。</template>
        </span>
        <button
          v-if="writable && deliverableId !== undefined"
          type="button"
          class="inline-button"
          :disabled="reassigning"
          @click="reassign"
        >{{ reassigning ? '正在重新派发…' : '按现在的团队重新派发' }}</button>
        <small v-else-if="!writable">这个项目你只有查看权限，请项目经理来处理。</small>
      </div>
    </template>

    <p v-else-if="route" class="route-hint route-manual">
      {{ route.blockers[0] || '这个任务还没有配置流转路径。' }}
    </p>
  </section>
</template>

<style scoped>
.route-comments {
  margin: 6px 0 0; padding: 0; list-style: none; display: grid; gap: 6px;
}
.route-comments li {
  display: grid; gap: 2px;
  padding: 7px 10px; border-left: 2px solid var(--line);
  background: var(--paper); border-radius: 0 var(--radius-s) var(--radius-s) 0;
}
.route-comments b { font-size: 12px; }
.route-comments b.approved { color: var(--green, #15803d); }
.route-comments b.rejected { color: var(--red, #b91c1c); }
.route-comments span { font-size: 12px; line-height: 1.7; white-space: pre-wrap; word-break: break-word; }
.route-comments em { font-style: normal; color: var(--muted); font-size: 11px; }
.route-diagram { display: grid; gap: 8px; }
.route-diagram > header { display: flex; align-items: baseline; gap: 8px; }
.route-diagram > header b { font-size: 13px; color: var(--ink); }
.route-diagram > header small { color: var(--muted); font-size: 12px; }
.route-hint { margin: 0; color: var(--muted); font-size: 12px; line-height: 1.7; }
.route-error { color: var(--risk); }
.route-manual {
  padding: 9px 11px; border-left: 3px solid var(--warning-graphic, #e8a13a);
  background: #fdf6ec; color: #7a4a10;
}

.route-track {
  display: flex; flex-wrap: wrap; align-items: stretch; gap: 4px;
  margin: 0; padding: 0; list-style: none;
}
.route-step { display: flex; align-items: center; gap: 4px; }
.route-arrow { color: var(--muted); font-style: normal; font-size: 14px; }
.route-card {
  display: grid; gap: 2px; min-width: 132px; padding: 8px 10px;
  border: 1px solid var(--line); border-radius: var(--radius-s); background: var(--white);
}
.route-order { color: var(--muted); font-size: 10px; font-weight: 800; letter-spacing: .04em; }
.route-card b { font-size: 13px; color: var(--ink); line-height: 1.4; }
.route-people { display: flex; flex-wrap: wrap; gap: 4px; font-size: 12px; color: var(--ink); }
.route-person { padding: 0 5px; border-radius: 4px; background: var(--paper); }
.route-person.signed { color: var(--green); background: rgba(38, 122, 99, .1); }
.route-person.stale { color: var(--risk); background: rgba(196, 63, 56, .1); text-decoration: line-through; }
.route-gaps {
  display: grid; gap: 5px; margin-top: 8px; padding: 9px 12px;
  border: 1px dashed var(--warning-graphic, #e8a13a); border-radius: var(--radius-s);
  background: #fdf6ec;
}
.route-gaps b { font-size: 13px; color: #7a4a10; }
.route-gaps ul { margin: 0; padding-left: 18px; }
.route-gaps li, .route-gaps span { color: #7a4a10; font-size: 12px; line-height: 1.7; }
.route-gap-flag { color: #b06a12; font-weight: 700; }
.route-stale { display: grid; gap: 5px; justify-items: start; margin-top: 8px; padding: 9px 12px; border: 1px dashed var(--risk); border-radius: var(--radius-s); background: rgba(196, 63, 56, .05); }
.route-stale b { font-size: 13px; color: var(--risk); }
.route-stale span, .route-stale small { color: var(--muted); font-size: 12px; line-height: 1.7; }
.route-people em { color: var(--risk); font-style: normal; font-weight: 700; }
.route-card small { color: var(--muted); font-size: 11px; }

/* 状态只用三种：走过的绿、当前蓝、还没到灰；派不到人的红边 */
.is-done .route-card { border-color: #bfe6d0; background: #f2fbf6; }
/* 只会派到提交人本人、会被整级跳过的级：虚线框压暗，名字划掉，不能画成「还要等这个人批」 */
.is-skipped .route-card { border-style: dashed; background: #f7f7f9; color: var(--muted); }
.is-skipped .route-people s { color: var(--muted); }
.route-skip-flag { color: #6b7280; font-weight: 700; font-size: 11px; margin-left: 4px; }
.is-current .route-card { border-color: var(--blue); border-width: 2px; box-shadow: 0 0 0 3px rgba(0, 117, 174, .1); }
.no-people .route-card { border-color: var(--risk); background: #fdf1f1; }
.route-now {
  justify-self: start; margin-top: 2px; padding: 1px 7px; border-radius: 999px;
  background: var(--blue); color: #fff; font-size: 10px; font-weight: 800;
}
.route-blockers { margin: 0; padding-left: 18px; color: var(--risk); font-size: 12px; line-height: 1.8; }
</style>
